import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    JjamesButtonComponent, JJamesCardComponent, JJamesLoadingComponent,
    PlateComponent, MAX_PLATE_LENGTH, PLATE_COUNTRIES, normalizePlate, sanitizePlateInput
} from 'shared-lib';
import { EasypassRedemptionResultDto, EasypassService, PublicEasypassDto } from '../../api/gen';

/**
 * A message held as its translation key, not as resolved text, so it follows a language switch like
 * the rest of the page (see LicensePlateRegistrationComponent).
 */
interface TranslatableMessage {
    key: string;
    params?: Record<string, unknown>;
}

/**
 * The stable `Error.Title` codes of RedeemEasypassHandler / FetchEasypassByTokenHandler. The body is
 * `Title + "." + Detail` with an English detail, so only the code is read and the text is our own.
 */
const ERROR_CODE_KEYS: Record<string, string> = {
    code_used: 'EASYPASS_REDEEM.TICKET_USED',
    code_expired: 'EASYPASS_REDEEM.TICKET_EXPIRED',
    code_not_active: 'EASYPASS_REDEEM.CODE_NOT_ACTIVE',
    code_not_needed: 'EASYPASS_REDEEM.CODE_NOT_NEEDED',
    code_stay_paid: 'EASYPASS_REDEEM.CODE_STAY_PAID',
    codes_only: 'EASYPASS_REDEEM.CODES_ONLY'
};

@Component({
    selector: 'app-easypass-redeem',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslatePipe,
        RouterModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatSnackBarModule,
        JjamesButtonComponent,
        JJamesCardComponent,
        JJamesLoadingComponent,
        PlateComponent
    ],
    templateUrl: './easypass-redeem.component.html',
    styleUrls: ['./easypass-redeem.component.scss']
})
export class EasypassRedeemComponent implements OnInit {

    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private fb = inject(FormBuilder);
    private easypassService = inject(EasypassService);
    private snackBar = inject(MatSnackBar);
    private translate = inject(TranslateService);

    token = signal<string | null>(null);
    easypassInfo = signal<PublicEasypassDto | null>(null);
    isLoadingInfo = signal(true);
    isLoading = signal(false);
    isSuccess = signal(false);
    /** What the pass now does for the car, worded from the redemption's answer. */
    successMessage = signal<TranslatableMessage | null>(null);
    successValidTo = signal<string | null>(null);
    isError = signal(false);
    errorMessage = signal<TranslatableMessage | null>(null);
    isNotFound = signal(false);

    redeemForm = this.fb.group({
        // Kept in step with sanitizePlateInput: the pattern accepts exactly what the input filter lets
        // through, so a filtered value can never be stuck invalid.
        licensePlate: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(MAX_PLATE_LENGTH),
            Validators.pattern(/^[A-Z0-9ÄÖÜ]+$/)]],
        email: [''],
        phone: ['']
    });

    /** The redemption endpoint takes no country, so the field renders the Austrian band. */
    protected readonly country = PLATE_COUNTRIES.find(c => c.code === 'AT') ?? PLATE_COUNTRIES[0];
    protected readonly maxPlateLength = MAX_PLATE_LENGTH;

    /**
     * Rejects illegal characters (`;`, `[`, `]`, quotes, emoji ...) as they are typed or pasted, and
     * restores the caret by hand so a stray character typed mid-plate does not throw the user to the
     * end of the field. Mirrors RegisterLicensePlateComponent.
     */
    protected onPlateFieldInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const raw = input.value;
        const cleaned = sanitizePlateInput(raw);
        if (cleaned === raw) {
            return;
        }

        const caret = input.selectionStart ?? raw.length;
        const rejectedBeforeCaret = caret - sanitizePlateInput(raw.slice(0, caret)).length;
        this.redeemForm.get('licensePlate')!.setValue(cleaned);
        input.value = cleaned;
        const restored = Math.max(0, caret - rejectedBeforeCaret);
        input.setSelectionRange(restored, restored);
    }

    allowedEntriesDisplay(): TranslatableMessage {
        const entries = this.easypassInfo()?.allowedEntries;
        if (entries === 0 || entries == null) return { key: 'EASYPASS_REDEEM.UNLIMITED' };
        return entries === 1
            ? { key: 'EASYPASS_REDEEM.SINGLE_ENTRY' }
            : { key: 'EASYPASS_REDEEM.ENTRIES', params: { count: entries } };
    }

    remainingCapacityDisplay(): TranslatableMessage {
        const info = this.easypassInfo();
        if (!info || info.isUnlimited) return { key: 'EASYPASS_REDEEM.UNLIMITED' };
        return {
            key: 'EASYPASS_REDEEM.CAPACITY_LEFT',
            params: { remaining: info.remainingCapacity ?? 0, total: info.maxRedemptions ?? 0 }
        };
    }

    /**
     * Inside: the open stay is now on the pass. Outside: the pass applies from the next entry. When the
     * backend could not tell (`coversOpenStay` null), neither is claimed.
     */
    private successFrom(result: EasypassRedemptionResultDto | null | undefined, plate: string): TranslatableMessage {
        const pays = !!result?.driverPays;
        const covers = result?.coversOpenStay;
        if (covers == null) return { key: 'EASYPASS_REDEEM.SUCCESS_ACTIVE', params: { plate } };
        if (pays) {
            return { key: covers ? 'EASYPASS_REDEEM.SUCCESS_PAYS_INSIDE' : 'EASYPASS_REDEEM.SUCCESS_PAYS_OUTSIDE', params: { plate } };
        }
        return { key: covers ? 'EASYPASS_REDEEM.SUCCESS_FREE_INSIDE' : 'EASYPASS_REDEEM.SUCCESS_FREE_OUTSIDE', params: { plate } };
    }

    /** The known error code of a failed call, or the fallback. */
    private errorFrom(err: unknown, fallbackKey: string): TranslatableMessage {
        const body = (err as { error?: unknown } | null | undefined)?.error;
        const code = typeof body === 'string' ? body.split('.', 1)[0].trim() : '';
        return { key: ERROR_CODE_KEYS[code] ?? fallbackKey };
    }

    ngOnInit(): void {
        const token = this.route.snapshot.paramMap.get('token');
        if (!token) {
            this.isError.set(true);
            this.errorMessage.set({ key: 'EASYPASS_REDEEM.NO_TOKEN' });
            this.isLoadingInfo.set(false);
            return;
        }
        this.token.set(token);

        // Fetch Easypass info to show expiry / entries before the user redeems
        // GET /easypasses/{token} is anonymous and answers the public Easypass shape only.
        this.easypassService.easypassesTokenGet(token).subscribe({
            next: (info) => {
                this.easypassInfo.set(info);
                this.isLoadingInfo.set(false);
                if (info.isExpired) {
                    this.isError.set(true);
                    this.errorMessage.set({ key: info.isExitTicket ? 'EASYPASS_REDEEM.TICKET_EXPIRED' : 'EASYPASS_REDEEM.EXPIRED' });
                } else if (info.isConsumed) {
                    this.isError.set(true);
                    this.errorMessage.set({
                        key: info.isExitTicket ? 'EASYPASS_REDEEM.TICKET_USED' : 'EASYPASS_REDEEM.FULLY_BOOKED_TEXT'
                    });
                }
            },
            error: (err) => {
                this.isLoadingInfo.set(false);
                if (err?.status === 404) {
                    this.isNotFound.set(true);
                } else {
                    this.isError.set(true);
                    // e.g. "codes_only": the shared link of an exit-ticket batch, which only its printed codes redeem.
                    this.errorMessage.set(this.errorFrom(err, 'EASYPASS_REDEEM.LOAD_FAILED'));
                }
            }
        });
    }

    onSubmit(): void {
        if (this.redeemForm.invalid || !this.token()) return;

        this.isLoading.set(true);
        this.isError.set(false);
        this.errorMessage.set(null);

        const licensePlate = normalizePlate(this.redeemForm.get('licensePlate')!.value);
        const email = this.redeemForm.get('email')!.value?.trim() || undefined;
        const phone = this.redeemForm.get('phone')!.value?.trim() || undefined;

        this.easypassService.easypassesRedeemPost({ token: this.token()!, licensePlate, email, phone }).subscribe({
            next: (result) => {
                this.isLoading.set(false);
                this.successMessage.set(this.successFrom(result, licensePlate));
                this.successValidTo.set(result?.validTo ?? this.easypassInfo()?.validTo ?? null);
                this.isSuccess.set(true);
                this.snackBar.open(
                    this.translate.instant('EASYPASS_REDEEM.SNACK_SUCCESS'),
                    this.translate.instant('EASYPASS_REDEEM.CLOSE'),
                    { duration: 5000 });
            },
            error: (err) => {
                this.isLoading.set(false);
                this.isError.set(true);
                this.errorMessage.set(this.errorFrom(err, 'EASYPASS_REDEEM.REDEEM_FAILED'));
            }
        });
    }
    HomeClicked(): void {
        this.router.navigate(['/']);
    }
}
